import React, { useCallback, useEffect, useState } from 'react';
import './Settings.css';
import { ApiKeyInfo, FreeTierInfo, ModelInfo, UsageByModel } from './types';
import ApiKeyCard from './ApiKeyCard';
import ModelPicker from './ModelPicker';
import UsageCard from './UsageCard';
import ChangePasswordCard from './ChangePasswordCard';
import AdminResetPasswordCard from './AdminResetPasswordCard';

interface SettingsUser {
  email: string;
  name?: string;
  institution?: string;
  department?: string;
  is_admin?: boolean;
}

interface SettingsViewProps {
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  user: SettingsUser | null;
  // Shown at the top when the user was sent here by the AI gate.
  notice?: string | null;
  onBack: () => void;
  // Keeps App-level gate state in sync after add/replace/delete.
  onApiKeyChanged: (apiKey: ApiKeyInfo | null) => void;
}

// Settings: account info, BYOK API key management, model choice, usage.
const SettingsView: React.FC<SettingsViewProps> = ({ apiBaseUrl, getAuthHeaders, user, notice, onBack, onApiKeyChanged }) => {
  const [apiKey, setApiKey] = useState<ApiKeyInfo | null>(null);
  const [freeTier, setFreeTier] = useState<FreeTierInfo | null>(null);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [usage, setUsage] = useState<UsageByModel[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [modelSaving, setModelSaving] = useState(false);

  const jsonHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', ...getAuthHeaders() }),
    [getAuthHeaders]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [keyRes, modelsRes, usageRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/user/api-key`, { headers: getAuthHeaders() }),
          fetch(`${apiBaseUrl}/api/models`, { headers: getAuthHeaders() }),
          fetch(`${apiBaseUrl}/api/user/usage`, { headers: getAuthHeaders() })
        ]);
        if (cancelled) return;
        if (keyRes.ok) {
          const data = await keyRes.json();
          setApiKey(data.apiKey || null);
          setFreeTier(data.freeTier || null);
        }
        if (modelsRes.ok) {
          const data = await modelsRes.json();
          setModels(data.models || []);
          setDefaultModel(data.default || '');
        }
        if (usageRes.ok) setUsage((await usageRes.json()).byModel || []);
      } catch (err) {
        console.error('Settings load failed:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  const saveKey = async (key: string, model: string): Promise<string | null> => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/user/api-key`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ key, default_model: model })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return data.error || 'Could not save the API key. Please try again.';
      setApiKey(data.apiKey);
      onApiKeyChanged(data.apiKey);
      return null;
    } catch {
      return 'Could not reach the server. Please try again.';
    }
  };

  const deleteKey = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/user/api-key`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setApiKey(null);
        onApiKeyChanged(null);
      }
    } catch (err) {
      console.error('Key delete failed:', err);
    }
  };

  const changeModel = async (modelId: string) => {
    if (!apiKey) return;
    const previous = apiKey.default_model;
    setApiKey({ ...apiKey, default_model: modelId }); // optimistic
    setModelSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/user/api-key/model`, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify({ default_model: modelId })
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json();
      setApiKey(data.apiKey);
      onApiKeyChanged(data.apiKey);
    } catch {
      setApiKey(prev => (prev ? { ...prev, default_model: previous } : prev));
      alert('Could not update the model preference. Please try again.');
    } finally {
      setModelSaving(false);
    }
  };

  return (
    <div className="settings-view">
      <div className="settings-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-icon">←</span>
          <span className="back-text">Main Menu</span>
        </button>
        <h2>Settings</h2>
      </div>

      {notice && <div className="settings-notice">{notice}</div>}

      {!loaded ? (
        <p className="settings-hint">Loading…</p>
      ) : (
        <div className="settings-cards">
          <section className="settings-card">
            <h3>Account</h3>
            <dl className="settings-profile">
              <dt>Email</dt><dd>{user?.email}</dd>
              {user?.name && (<><dt>Name</dt><dd>{user.name}</dd></>)}
              {user?.institution && (<><dt>Institution</dt><dd>{user.institution}</dd></>)}
              {user?.department && (<><dt>Department</dt><dd>{user.department}</dd></>)}
            </dl>
            {user?.is_admin && (
              <p className="settings-hint">
                Admin account: without a personal key, AI requests use the system key (budget-capped), even if the free tier is off.
              </p>
            )}
          </section>

          <ChangePasswordCard apiBaseUrl={apiBaseUrl} getAuthHeaders={getAuthHeaders} />

          <ApiKeyCard
            apiKey={apiKey}
            freeTier={freeTier}
            models={models}
            defaultModel={defaultModel}
            onSave={saveKey}
            onDelete={deleteKey}
          />

          {apiKey && (
            <section className="settings-card">
              <h3>Model</h3>
              <p className="settings-hint">Used for Agent Mode, Guided Mode, and AI assistance in Protocol View.</p>
              <ModelPicker
                models={models}
                value={apiKey.default_model}
                disabled={modelSaving}
                onChange={changeModel}
              />
            </section>
          )}

          <UsageCard usage={usage} models={models} />

          {user?.is_admin && (
            <AdminResetPasswordCard apiBaseUrl={apiBaseUrl} getAuthHeaders={getAuthHeaders} />
          )}

          <section className="settings-card">
            <h3>Privacy</h3>
            <p className="settings-hint">
              AI requests go to the Anthropic API. Anthropic's terms indicate API data is not used to train
              future models — see the{' '}
              <a href="https://privacy.claude.com/en/articles/7996885-how-do-you-use-personal-data-in-model-training" target="_blank" rel="noopener noreferrer">
                Anthropic Privacy Policy
              </a>.
            </p>
          </section>
        </div>
      )}
    </div>
  );
};

export default SettingsView;
